import React, { Fragment, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Container, Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ArrowLeft, Edit2, Calendar, AlertTriangle } from "react-feather";
import Loader from "../../shared/Loader";
import { errorToast, successToast } from "../../shared/Toast";
import {
  editBranch,
  fetchBranches,
  getGoogleBusinessLocations,
} from "../../utils/apiCalls";
import ToggleSwitch from "../../shared/ToggleSwitch";
import Breadcrumb from "../common/breadcrumb";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, SectionTitle } from "../reporting/ReportingShared";
import MultiSelectField from "../common/MultiSelectField";
import { getBrands, getBranchBrandIds } from "../../utils/brandApi";
import { coordinateStatus } from "../../utils/coordinateStatus";
import BranchFulfillment from "./branch-fulfillment";
import BranchImagesField, { MAX_BRANCH_IMAGES, toGalleryItems } from "./BranchImagesField";

const EditBranch = () => {
  const navigate = useNavigate();
  const weekMapping = Array(7).fill(null).map((_, i) => ({
    weekday: i,
    isClosed: true,
    is24Hours: false,
    openTime: "",
    closeTime: "",
  }));
  const { branchID } = useParams();
  const [loading, setLoading] = useState(false);
  // Ordered gallery (first = cover). `savedImagePaths` is what the server holds, so an
  // untouched gallery is not re-sent on save.
  const [galleryItems, setGalleryItems] = useState([]);
  const [savedImagePaths, setSavedImagePaths] = useState([]);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [externalLocationIdLocked, setExternalLocationIdLocked] = useState(false);
  const [originalExternalLocationId, setOriginalExternalLocationId] = useState("");
  // Which section of the branch page is showing. The fulfillment settings are a separate
  // record with their own save, so they get their own tab rather than sitting under the
  // branch form where a reader would expect the form's Save button to cover them.
  const [activeSection, setActiveSection] = useState("details");
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  // Consumer brands this branch carries. Loaded and saved separately from branchData
  // because they are a junction table, not a branch column — and kept out of the branch
  // list payload, so they need their own by-id fetch.
  const [brandOptions, setBrandOptions] = useState([]);
  // Google listings are fetched on demand, not on page load: it is a live Google API call and
  // most branch edits have nothing to do with Google.
  const [googleLocations, setGoogleLocations] = useState(null);
  const [loadingGoogleLocations, setLoadingGoogleLocations] = useState(false);
  const [branchMerchantId, setBranchMerchantId] = useState(null);
  const [selectedBrandIds, setSelectedBrandIds] = useState([]);
  const [branchData, setBranchData] = useState({
    name: "",
    arabicName: "",
    address: "",
    arabicAddress: "",
    lat: "",
    lng: "",
    openingTime: "",
    closingTime: "",
    orderCutoffMinutes: 2,
    active: false,
    snooze_online_orders: false,
    is24Hours: false,
    externalLocationId: "",
    brandReference: "",
    buzzerTopic: "",
    googleLocationName: "",
    googleMenuPushEnabled: true,
    kdsApp: true,
    kdsWeb: false,
    city: "",
    email: "",
    phone: "",
    flagShipStore: false,
    carPickupEnabled: true,
    menuPaymentMode: "cash",
    weeklySchedules: weekMapping,
  });
  const weekdays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  useEffect(() => {
    const fetchBranchesAndSetData = async () => {
      setLoading(true);
      try {
        const response = await fetchBranches({}, false);
        if (response.isError === false) {
          const branch = response?.data?.find((b) => b.id === parseInt(branchID));
          if (branch) {
            const defaultSchedules = weekMapping;
            const fetchedSchedules = branch.weeklySchedules || [];
            const mergedSchedules = defaultSchedules.map((day) => {
              const found = fetchedSchedules.find((s) => s.weekday === day.weekday);
              return found ? { ...day, ...found } : day;
            });
            setBranchData({
              name: branch.name || "",
              arabicName: branch.arabicName || "",
              address: branch.address || "",
              arabicAddress: branch.arabicAddress || "",
              lat: branch.lat || "",
              lng: branch.lng || "",
              openingTime: branch.opening_time || "",
              closingTime: branch.closing_time || "",
              orderCutoffMinutes: branch.orderCutoffMinutes ?? 2,
              active: branch.active,
              snooze_online_orders: branch.snooze_online_orders,
              is24Hours: branch.is24Hours,
              flagShipStore: branch.flagShipStore || false,
              carPickupEnabled: !branch.disableCarPickup,
              city: branch.city || "",
              phone: branch.phone || "",
              email: branch.email || "",
              externalLocationId: branch.externalLocationId || "",
              brandReference: branch.brandReference || "",
              buzzerTopic: branch.buzzerTopic || "",
              googleLocationName: branch.googleLocationName || "",
              googleMenuPushEnabled: branch.googleMenuPushEnabled ?? true,
              kdsApp: branch.kdsApp ?? true,
              kdsWeb: branch.kdsWeb ?? false,
              menuPaymentMode: branch.menuPaymentMode || "cash",
              weeklySchedules: mergedSchedules,
            });
            const extLocId = branch.externalLocationId || "";
            setOriginalExternalLocationId(extLocId);
            setExternalLocationIdLocked(!!extLocId);
            const imagePaths = branch.images?.length ? branch.images : branch.image ? [branch.image] : [];
            setSavedImagePaths(imagePaths);
            setGalleryItems(toGalleryItems(imagePaths));

            // Brands: options come from the branch's own merchant, current tags from the
            // by-id endpoint (the list payload deliberately omits them).
            setBranchMerchantId(branch.MerchantId || null);
            if (branch.MerchantId) {
              const brands = await getBrands(branch.MerchantId, { active: true });
              setBrandOptions((brands || []).map((b) => ({ value: b.id, label: b.name })));
            }
            setSelectedBrandIds(await getBranchBrandIds(branchID));
          } else {
            errorToast("Branch not found");
            navigate("/branches/branchesList");
          }
        } else {
          errorToast("Failed to fetch branches");
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchBranchesAndSetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchID, navigate]);

  const handleInputChange = (e, key) => {
    setBranchData((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleScheduleChange = (dayIndex, field, value) => {
    const updatedSchedules = [...branchData?.weeklySchedules];
    const daySchedule = { ...updatedSchedules?.find((s) => s?.weekday === dayIndex), [field]: value };

    if (field === "isClosed" && value) {
      daySchedule.is24Hours = false;
      daySchedule.openTime = "";
      daySchedule.closeTime = "";
    } else if (field === "is24Hours" && value) {
      daySchedule.isClosed = false;
      daySchedule.openTime = "";
      daySchedule.closeTime = "";
    }

    const scheduleIndex = updatedSchedules?.findIndex((s) => s?.weekday === dayIndex);
    updatedSchedules[scheduleIndex] = daySchedule;

    setBranchData((prev) => ({ ...prev, weeklySchedules: updatedSchedules }));
  };

  // Loaded only when asked. The endpoint calls Google (cached server-side for ten minutes), so it
  // must not fire just because someone opened a branch to change its phone number.
  const loadGoogleLocations = async () => {
    if (!branchMerchantId) {
      errorToast("This branch has no merchant");
      return;
    }
    setLoadingGoogleLocations(true);
    const data = await getGoogleBusinessLocations(branchMerchantId);
    setGoogleLocations(data?.locations || []);
    setLoadingGoogleLocations(false);
  };

  const buildFormData = () => {
    const formData = new FormData();
    // First: multer picks the upload folder from dataType, and it only sees fields that
    // arrived before the file.
    formData.append("dataType", "branch");
    formData.append("merchantID", branchData.merchantID);
    formData.append("name", branchData.name);
    formData.append("arabicName", branchData.arabicName);
    formData.append("address", branchData.address);
    formData.append("arabicAddress", branchData.arabicAddress);
    formData.append("lat", `${branchData.lat}`);
    formData.append("lng", `${branchData.lng}`);
    formData.append("openingTime", branchData.openingTime);
    formData.append("closingTime", branchData.closingTime);
    formData.append("orderCutoffMinutes", branchData.orderCutoffMinutes);
    formData.append("active", branchData.active);
    formData.append("city", branchData.city);
    formData.append("phone", branchData.phone);
    formData.append("email", branchData.email);
    formData.append("flagShipStore", branchData.flagShipStore);
    // Positive toggle in the UI, negative flag in the backend — invert once here.
    formData.append("disableCarPickup", !branchData.carPickupEnabled);
    formData.append("snooze_online_orders", branchData.snooze_online_orders);
    formData.append("is24Hours", branchData.is24Hours);
    if (branchData.externalLocationId) formData.append("externalLocationId", branchData.externalLocationId);
    formData.append("brandReference", branchData.brandReference || "");
    formData.append("buzzerTopic", branchData.buzzerTopic || "");
    formData.append("googleLocationName", branchData.googleLocationName || "");
    formData.append("googleMenuPushEnabled", branchData.googleMenuPushEnabled);
    formData.append("kdsApp", branchData.kdsApp);
    formData.append("kdsWeb", branchData.kdsWeb);
    formData.append("menuPaymentMode", branchData.menuPaymentMode || "cash");
    // Comma-joined by FormData, which is the wire format the backend's parseBrandIds
    // expects. Always sent, so clearing the selection actually clears the tags — the
    // backend treats an absent key as "leave alone" and an empty one as "clear".
    formData.append("brandIds", selectedBrandIds);
    if (JSON.stringify(branchData.weeklySchedules) !== JSON.stringify(weekMapping)) {
      formData.append("weeklySchedules", JSON.stringify(branchData.weeklySchedules));
    }
    // The layout is the full ordered gallery: kept images by path, new files by their index
    // among the `images` uploads. Only sent when the gallery changed.
    const galleryChanged =
      galleryItems.some((i) => i.kind === "new") ||
      galleryItems.map((i) => i.path).join("|") !== savedImagePaths.join("|");
    if (galleryChanged) {
      let uploadIndex = 0;
      const layout = galleryItems.map((item) => {
        if (item.kind === "existing") return { existing: item.path };
        formData.append("images", item.file);
        return { upload: uploadIndex++ };
      });
      formData.append("imagesLayout", JSON.stringify(layout));
    }
    return formData;
  };

  const submitForm = async (formData) => {
    try {
      setLoading(true);
      await editBranch(branchID, formData);
      successToast("Branch updated successfully");
      navigate("/branches/branchesList");
    } catch (error) {
      console.error("Error updating branch:", error);
      errorToast("Failed to update branch");
    } finally {
      setLoading(false);
    }
  };

  const handleValidSubmit = async (e) => {
    e.preventDefault();
    const formData = buildFormData();

    if (branchData.externalLocationId !== originalExternalLocationId) {
      setPendingFormData(formData);
      setConfirmModalOpen(true);
      return;
    }

    await submitForm(formData);
  };

  const handleConfirmSave = async () => {
    setConfirmModalOpen(false);
    if (pendingFormData) {
      await submitForm(pendingFormData);
      setPendingFormData(null);
    }
  };

  const handleDiscard = () => navigate("/branches/branchesList");

  // ---- Styles ----
  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: C.textLight,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
    display: "block",
  };
  const inputStyle = {
    padding: "10px 12px",
    fontSize: 13,
    borderRadius: 8,
    border: `1.5px solid ${C.border}`,
    fontFamily: "inherit",
    outline: "none",
    color: C.text,
    background: C.white,
    width: "100%",
    transition: "border 0.15s",
  };
  const invalidInputStyle = { ...inputStyle, border: `1.5px solid ${C.orange}`, background: C.orangeLight };

  // Judged on what is currently TYPED, not on what was loaded, so the warning clears the
  // moment a real coordinate is entered. `latitude` is deliberately not passed through: it
  // is the server's derived value for the SAVED row and would report "not indexed" against
  // edits that have not been submitted yet.
  const coordStatus = coordinateStatus({ lat: branchData.lat, lng: branchData.lng });

  const primaryBtnStyle = {
    background: `linear-gradient(135deg, ${C.coral}, #00bfa5)`,
    border: "none",
    borderRadius: 8,
    padding: "10px 24px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(0, 150, 137,0.25)",
    cursor: "pointer",
    fontSize: 13,
  };
  const ghostBtnStyle = {
    background: C.white,
    border: `1.5px solid ${C.border}`,
    borderRadius: 8,
    padding: "10px 24px",
    fontWeight: 600,
    color: C.text,
    cursor: "pointer",
    fontSize: 13,
  };
  const infoBtnStyle = {
    background: `linear-gradient(135deg, ${C.blue}, #A8B8FF)`,
    border: "none",
    borderRadius: 8,
    padding: "10px 20px",
    fontWeight: 600,
    color: C.white,
    boxShadow: "0 4px 12px rgba(102,126,234,0.25)",
    cursor: "pointer",
    fontSize: 13,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  };
  const toggleRowStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 14px",
    borderRadius: 10,
    border: `1px solid ${C.border}`,
    background: C.white,
  };
  const toggleTitleStyle = { fontSize: 13, fontWeight: 600, color: C.text };
  const toggleHintStyle = { fontSize: 11, color: C.textLight };
  const fieldHintStyle = { fontSize: 11, color: C.textLight, marginTop: 4 };
  const fieldGridStyle = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 240px), 1fr))",
    gap: 16,
  };
  const toggleGridStyle = { ...fieldGridStyle, gap: 12 };

  return (
    <Fragment>
      <Breadcrumb title="Edit Branch" parent="Branch" />
      <Container fluid>
        <style>
          {reportingGlobalCSS}
          {`.branch-edit-layout { display: grid; grid-template-columns: 320px minmax(0, 1fr); gap: 20px; align-items: start; }
            @media (max-width: 991px) { .branch-edit-layout { grid-template-columns: minmax(0, 1fr); } }`}
        </style>
        <div className="rpt-wrap">
          {!loading && (
            <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: `1px solid ${C.border}` }}>
              {[
                { key: "details", label: "Branch Details" },
                { key: "fulfillment", label: "Curbside & Drive-Thru" },
              ].map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveSection(t.key)}
                  style={{
                    background: "none",
                    border: "none",
                    borderBottom: `2px solid ${activeSection === t.key ? C.coral : "transparent"}`,
                    color: activeSection === t.key ? C.coral : C.textMed,
                    fontWeight: activeSection === t.key ? 700 : 500,
                    fontSize: 13,
                    padding: "10px 16px",
                    cursor: "pointer",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}

          {loading ? (
            <Loader />
          ) : activeSection === "fulfillment" ? (
            <BranchFulfillment branchId={branchID} />
          ) : (
            <form onSubmit={handleValidSubmit}>
              {/* Heading */}
              <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
                <div
                  onClick={handleDiscard}
                  title="Back"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 10,
                    border: `1.5px solid ${C.border}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    background: C.white,
                  }}
                >
                  <ArrowLeft size={16} color={C.textMed} />
                </div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color: C.text, letterSpacing: -0.3 }}>
                    Edit Branch
                  </div>
                  <div style={{ fontSize: 12, color: C.textMed, marginTop: 2 }}>
                    {branchData.name || "Loading..."}
                  </div>
                </div>
              </div>

              <div className="branch-edit-layout">
                {/* LEFT: images + status */}
                <div>
                  <RCard>
                    <SectionTitle
                      right={
                        <span style={{ fontSize: 12, fontWeight: 600, color: C.textLight }}>
                          {galleryItems.length} / {MAX_BRANCH_IMAGES}
                        </span>
                      }
                    >
                      Images
                    </SectionTitle>
                    <BranchImagesField items={galleryItems} onChange={setGalleryItems} />
                  </RCard>

                  <RCard>
                    <SectionTitle>Status</SectionTitle>
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={toggleRowStyle}>
                        <div>
                          <div style={toggleTitleStyle}>Active</div>
                          <div style={toggleHintStyle}>Open for business</div>
                        </div>
                        <ToggleSwitch
                          checked={branchData.active}
                          handleToggle={(isChecked) => setBranchData((prev) => ({ ...prev, active: isChecked }))}
                        />
                      </div>
                      <div style={toggleRowStyle}>
                        <div>
                          <div style={toggleTitleStyle}>Snooze Online Orders</div>
                          <div style={toggleHintStyle}>Pause online ordering</div>
                        </div>
                        <ToggleSwitch
                          checked={branchData.snooze_online_orders}
                          handleToggle={(isChecked) =>
                            setBranchData((prev) => ({ ...prev, snooze_online_orders: isChecked }))
                          }
                        />
                      </div>
                      <div style={toggleRowStyle}>
                        <div style={toggleTitleStyle}>Flagship Store</div>
                        <ToggleSwitch
                          checked={branchData.flagShipStore}
                          handleToggle={(isChecked) => setBranchData((prev) => ({ ...prev, flagShipStore: isChecked }))}
                        />
                      </div>
                    </div>
                  </RCard>
                </div>

                {/* RIGHT: details */}
                <div>
                  <RCard>
                    <SectionTitle>Basic Info</SectionTitle>
                    <div style={fieldGridStyle}>
                      <div>
                        <label style={labelStyle}>Name *</label>
                        <input
                          type="text"
                          value={branchData.name}
                          onChange={(e) => handleInputChange(e, "name")}
                          required
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>Arabic Name *</label>
                        <input
                          type="text"
                          value={branchData.arabicName}
                          onChange={(e) => handleInputChange(e, "arabicName")}
                          required
                          style={inputStyle}
                        />
                      </div>

                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>Address *</label>
                        <input
                          type="text"
                          value={branchData.address}
                          onChange={(e) => handleInputChange(e, "address")}
                          required
                          style={inputStyle}
                        />
                      </div>

                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>Arabic Address *</label>
                        <input
                          type="text"
                          value={branchData.arabicAddress}
                          onChange={(e) => handleInputChange(e, "arabicAddress")}
                          required
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>City</label>
                        <input
                          type="text"
                          value={branchData.city}
                          onChange={(e) => handleInputChange(e, "city")}
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>Phone</label>
                        <input
                          type="text"
                          value={branchData.phone}
                          onChange={(e) => handleInputChange(e, "phone")}
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>Email</label>
                        <input
                          type="text"
                          value={branchData.email}
                          onChange={(e) => handleInputChange(e, "email")}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Location</SectionTitle>
                    <div style={fieldGridStyle}>
                      <div>
                        <label style={labelStyle}>Latitude *</label>
                        <input
                          type="text"
                          value={branchData.lat}
                          onChange={(e) => handleInputChange(e, "lat")}
                          required
                          placeholder="24.7136"
                          style={coordStatus.ok ? inputStyle : invalidInputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>Longitude *</label>
                        <input
                          type="text"
                          value={branchData.lng}
                          onChange={(e) => handleInputChange(e, "lng")}
                          required
                          placeholder="46.6753"
                          style={coordStatus.ok ? inputStyle : invalidInputStyle}
                        />
                      </div>

                      {/* Live, because the form accepts these as free text and always has. The
                          values that break nearest-branch ranking — 0/0, 123, 1111 — all look
                          plausible in a text box, and nothing else on this screen would tell you
                          the branch had gone missing from the app's location list. */}
                      <div style={{ gridColumn: "1 / -1", marginTop: -6 }}>
                        {!coordStatus.ok ? (
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 8,
                              padding: "10px 14px",
                              borderRadius: 8,
                              background: coordStatus.tone === "orange" ? C.orangeLight : C.coralLight,
                              border: `1px solid ${coordStatus.tone === "orange" ? C.orange : C.coral}33`,
                              fontSize: 12,
                              color: C.text,
                              lineHeight: 1.5,
                            }}
                          >
                            <AlertTriangle
                              size={14}
                              color={coordStatus.tone === "orange" ? C.orange : C.coral}
                              style={{ flexShrink: 0, marginTop: 2 }}
                            />
                            <span>{coordStatus.hint}</span>
                          </div>
                        ) : (
                          <div style={{ fontSize: 11.5, color: C.textLight }}>
                            Decimal degrees —{" "}
                            <a
                              href={`https://www.google.com/maps?q=${encodeURIComponent(`${branchData.lat},${branchData.lng}`)}`}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: C.coral, fontWeight: 600 }}
                            >
                              check it on the map
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Hours</SectionTitle>
                    <div style={fieldGridStyle}>
                      <div>
                        <label style={labelStyle}>Opening Time *</label>
                        <input
                          type="time"
                          value={branchData.openingTime}
                          onChange={(e) => handleInputChange(e, "openingTime")}
                          required
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>Closing Time *</label>
                        <input
                          type="time"
                          disabled={!branchData.openingTime}
                          value={branchData.closingTime}
                          onChange={(e) => handleInputChange(e, "closingTime")}
                          required
                          style={{
                            ...inputStyle,
                            opacity: branchData.openingTime ? 1 : 0.55,
                          }}
                        />
                        {branchData.openingTime &&
                          branchData.closingTime &&
                          branchData.closingTime <= branchData.openingTime && (
                            <div style={{ color: C.blue, fontSize: 11, marginTop: 6, fontWeight: 600 }}>
                              Closes next day (overnight hours)
                            </div>
                          )}
                      </div>

                      <div>
                        <label style={labelStyle}>Order Cut-off (minutes)</label>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={branchData.orderCutoffMinutes}
                          onChange={(e) => handleInputChange(e, "orderCutoffMinutes")}
                          style={inputStyle}
                        />
                      </div>
                    </div>
                    <div style={{ ...toggleGridStyle, marginTop: 16 }}>
                      <div style={toggleRowStyle}>
                        <div>
                          <div style={toggleTitleStyle}>24 Hours</div>
                          <div style={toggleHintStyle}>Open around the clock</div>
                        </div>
                        <ToggleSwitch
                          checked={branchData.is24Hours}
                          handleToggle={(isChecked) => setBranchData((prev) => ({ ...prev, is24Hours: isChecked }))}
                        />
                      </div>
                      <div style={toggleRowStyle}>
                        <div>
                          <div style={toggleTitleStyle}>Weekly Schedule</div>
                          <div style={toggleHintStyle}>Per-day opening hours</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setScheduleModalOpen(true)}
                          style={infoBtnStyle}
                        >
                          <Calendar size={14} /> Manage
                        </button>
                      </div>
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Ordering &amp; Payment</SectionTitle>
                    <div style={toggleGridStyle}>
                      <div style={toggleRowStyle}>
                        <div>
                          <div style={toggleTitleStyle}>Car Pickup</div>
                          <div style={toggleHintStyle}>Off = in-store pickup only</div>
                        </div>
                        <ToggleSwitch
                          checked={branchData.carPickupEnabled}
                          handleToggle={(isChecked) => setBranchData((prev) => ({ ...prev, carPickupEnabled: isChecked }))}
                        />
                      </div>
                      <div style={{ ...toggleRowStyle, gap: 12, flexWrap: "wrap" }}>
                        <div>
                          <div style={toggleTitleStyle}>Menu Payment</div>
                          <div style={toggleHintStyle}>QR menu checkout</div>
                          {branchData.menuPaymentMode !== "cash" && (
                            <div style={{ ...toggleHintStyle, color: C.orange, marginTop: 2 }}>
                              Needs Telr HPP key on merchant
                            </div>
                          )}
                        </div>
                        <select
                          style={{ ...inputStyle, width: "auto", minWidth: 160 }}
                          value={branchData.menuPaymentMode}
                          onChange={(e) => setBranchData((prev) => ({ ...prev, menuPaymentMode: e.target.value }))}
                        >
                          <option value="cash">Pay at counter</option>
                          <option value="online">Online only (Telr)</option>
                          <option value="both">Customer chooses</option>
                        </select>
                      </div>
                    </div>
                  </RCard>

                  <RCard>
                    <SectionTitle>Integrations</SectionTitle>
                    <div style={fieldGridStyle}>
                      <div>
                        {/* Renamed from "Brand Reference": this is a POS/Deliverect merchant
                            slug, nothing to do with the Brands field below, and the old label
                            made the two look related. */}
                        <label style={labelStyle}>POS Brand Slug</label>
                        <input
                          type="text"
                          value={branchData.brandReference}
                          onChange={(e) => handleInputChange(e, "brandReference")}
                          placeholder="Optional"
                          style={inputStyle}
                        />
                        <div style={fieldHintStyle}>POS/KDS routing key — not a brand.</div>
                      </div>

                      <div>
                        <label style={labelStyle}>Brands</label>
                        <MultiSelectField
                          options={brandOptions}
                          value={brandOptions.filter((o) => selectedBrandIds.includes(o.value))}
                          onChange={(vals) => setSelectedBrandIds((vals || []).map((v) => v.value))}
                          placeholder={
                            brandOptions.length
                              ? "Select the brands sold here..."
                              : "No active brands for this merchant"
                          }
                          isDisabled={brandOptions.length === 0}
                        />
                        <div style={fieldHintStyle}>Brand-based apps only show tagged branches.</div>
                      </div>

                      <div>
                        <label style={labelStyle}>Buzzer Topic</label>
                        <input
                          type="text"
                          value={branchData.buzzerTopic}
                          onChange={(e) => handleInputChange(e, "buzzerTopic")}
                          placeholder="MQTT topic (unique per merchant)"
                          style={inputStyle}
                        />
                      </div>

                      <div>
                        <label style={labelStyle}>External Location ID</label>
                        <div style={{ position: "relative" }}>
                          <input
                            type="text"
                            value={branchData.externalLocationId}
                            onChange={(e) => handleInputChange(e, "externalLocationId")}
                            disabled={externalLocationIdLocked}
                            style={{
                              ...inputStyle,
                              paddingRight: 40,
                              background: externalLocationIdLocked ? C.bg : C.white,
                              opacity: externalLocationIdLocked ? 0.85 : 1,
                            }}
                          />
                          {externalLocationIdLocked && (
                            <Edit2
                              onClick={() => setExternalLocationIdLocked(false)}
                              size={16}
                              title="Click to edit"
                              style={{
                                position: "absolute",
                                right: 12,
                                top: "50%",
                                transform: "translateY(-50%)",
                                cursor: "pointer",
                                color: C.green,
                              }}
                            />
                          )}
                        </div>
                      </div>

                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>Kitchen Display (KDS)</label>
                        <div style={{ display: "flex", gap: "12px 28px", alignItems: "center", paddingTop: 6, flexWrap: "wrap" }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, fontWeight: 500 }}>
                            <ToggleSwitch checked={!!branchData.kdsApp} handleToggle={(v) => setBranchData((prev) => ({ ...prev, kdsApp: v }))} />
                            <span>KDS App (LAN)</span>
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, fontWeight: 500 }}>
                            <ToggleSwitch checked={!!branchData.kdsWeb} handleToggle={(v) => setBranchData((prev) => ({ ...prev, kdsWeb: v }))} />
                            <span>KDS Web (online)</span>
                          </label>
                        </div>
                      </div>

                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={labelStyle}>Google Business Location</label>
                        <input
                          type="text"
                          value={branchData.googleLocationName}
                          onChange={(e) => handleInputChange(e, "googleLocationName")}
                          placeholder="accounts/{accountId}/locations/{locationId}"
                          style={inputStyle}
                        />
                        <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
                          <button
                            type="button"
                            onClick={loadGoogleLocations}
                            disabled={loadingGoogleLocations}
                            style={{ padding: "6px 12px", borderRadius: 8, border: `1px solid ${C.border}`, background: C.white, color: C.text, fontWeight: 600, fontSize: 12, cursor: "pointer" }}
                          >
                            {loadingGoogleLocations ? "Loading…" : "Choose from Google"}
                          </button>
                          {googleLocations && (
                            <select
                              style={{ ...inputStyle, flex: 1, minWidth: "min(100%, 220px)" }}
                              value={branchData.googleLocationName}
                              onChange={(e) => setBranchData((prev) => ({ ...prev, googleLocationName: e.target.value }))}
                            >
                              <option value="">Not mapped</option>
                              {googleLocations.map((loc) => (
                                <option
                                  key={loc.name}
                                  value={loc.name}
                                  // A listing already claimed by another branch is shown but not
                                  // selectable — two branches writing one menu is a fight, not a feature.
                                  disabled={!!loc.mappedBranch && loc.mappedBranch.id !== Number(branchID)}
                                >
                                  {loc.title}
                                  {loc.storeCode ? ` · ${loc.storeCode}` : ""}
                                  {loc.canHaveFoodMenus ? "" : " · no food menu"}
                                  {loc.mappedBranch && loc.mappedBranch.id !== Number(branchID) ? ` · taken by ${loc.mappedBranch.name}` : ""}
                                </option>
                              ))}
                            </select>
                          )}
                          <label style={{ display: "flex", alignItems: "center", gap: 8, margin: 0, fontWeight: 500 }}>
                            <ToggleSwitch
                              checked={!!branchData.googleMenuPushEnabled}
                              handleToggle={(v) => setBranchData((prev) => ({ ...prev, googleMenuPushEnabled: v }))}
                            />
                            <span>Push menu to Google</span>
                          </label>
                        </div>
                        <div style={fieldHintStyle}>Menu is pushed here after every sync. Leave empty to stay off Google.</div>
                      </div>
                    </div>
                  </RCard>

                  <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginBottom: 20 }}>
                    <button type="button" onClick={handleDiscard} style={ghostBtnStyle}>
                      Discard
                    </button>
                    <button type="submit" style={primaryBtnStyle}>
                      Save
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </Container>

      {/* Weekly Schedule Modal */}
      <Modal isOpen={scheduleModalOpen} toggle={() => setScheduleModalOpen(false)} size="lg" centered>
        <ModalHeader toggle={() => setScheduleModalOpen(false)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>Manage Weekly Schedule</span>
        </ModalHeader>
        <ModalBody style={{ padding: 16 }}>
          <div
            style={{
              overflowX: "auto",
              borderRadius: 10,
              border: `1px solid ${C.border}`,
            }}
          >
            <table style={tbl.table}>
              <thead>
                <tr>
                  <th style={tbl.th}>Day</th>
                  <th style={{ ...tbl.th, textAlign: "center" }}>Status</th>
                  <th style={tbl.th}>Open Time</th>
                  <th style={tbl.th}>Close Time</th>
                </tr>
              </thead>
              <tbody>
                {weekdays?.map((day, index) => {
                  const schedule = branchData?.weeklySchedules?.find((s) => s?.weekday === index) || {};
                  const dimmed = schedule?.isClosed || schedule?.is24Hours;
                  return (
                    <tr key={index} className="rpt-row">
                      <td style={{ ...tbl.td, fontWeight: 700, color: C.text }}>{day}</td>
                      <td style={tbl.td}>
                        <div style={{ display: "flex", justifyContent: "center", gap: 20 }}>
                          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: C.text }}>
                            <input
                              type="checkbox"
                              checked={schedule?.isClosed || false}
                              onChange={(e) => handleScheduleChange(index, "isClosed", e.target.checked)}
                              style={{ width: 15, height: 15, accentColor: C.coral }}
                            />
                            Closed
                          </label>
                          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: C.text }}>
                            <input
                              type="checkbox"
                              checked={schedule?.is24Hours || false}
                              onChange={(e) => handleScheduleChange(index, "is24Hours", e.target.checked)}
                              style={{ width: 15, height: 15, accentColor: C.blue }}
                            />
                            24 Hours
                          </label>
                        </div>
                      </td>
                      <td style={tbl.td}>
                        <input
                          type="time"
                          value={schedule?.openTime || ""}
                          disabled={dimmed}
                          onChange={(e) => handleScheduleChange(index, "openTime", e.target.value)}
                          style={{
                            ...inputStyle,
                            padding: "7px 10px",
                            fontSize: 12,
                            opacity: dimmed ? 0.45 : 1,
                          }}
                        />
                      </td>
                      <td style={tbl.td}>
                        <input
                          type="time"
                          value={schedule?.closeTime || ""}
                          disabled={dimmed}
                          onChange={(e) => handleScheduleChange(index, "closeTime", e.target.value)}
                          style={{
                            ...inputStyle,
                            padding: "7px 10px",
                            fontSize: 12,
                            opacity: dimmed ? 0.45 : 1,
                          }}
                        />
                        {!dimmed &&
                          schedule?.openTime &&
                          schedule?.closeTime &&
                          schedule?.closeTime <= schedule?.openTime && (
                            <div style={{ color: C.blue, fontSize: 10, marginTop: 4, fontWeight: 600 }}>
                              Next day
                            </div>
                          )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setScheduleModalOpen(false)}>
            Cancel
          </Button>
          <button onClick={() => setScheduleModalOpen(false)} style={primaryBtnStyle}>
            Done
          </button>
        </ModalFooter>
      </Modal>

      {/* External Location Change Confirmation */}
      <Modal isOpen={confirmModalOpen} toggle={() => setConfirmModalOpen(false)} centered>
        <ModalHeader toggle={() => setConfirmModalOpen(false)} style={{ borderBottom: `2px solid ${C.borderLight}` }}>
          <span style={{ fontWeight: 700, color: C.text }}>Confirm Change</span>
        </ModalHeader>
        <ModalBody style={{ padding: 24, color: C.textMed, fontSize: 13 }}>
          Are you sure you want to change the External Location ID? This is sensitive information.
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => {
              setConfirmModalOpen(false);
              setPendingFormData(null);
            }}
          >
            Cancel
          </Button>
          <button onClick={handleConfirmSave} style={primaryBtnStyle}>
            Yes, Save
          </button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default EditBranch;
